import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import { IconButton } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useState } from 'react';

const dynamicAutoCollapse = (
  collapseAfter: number | undefined,
  content: React.ReactNode,
): boolean => {
  if (collapseAfter === undefined) return false;
  const length = Array.isArray(content) ? content.length : 1;

  return length > collapseAfter;
};

interface Props {
  className?: string;

  /**
   * Element to render to the left of the chevron button.
   * Always displayed
   */
  header: React.ReactNode;

  /**
   * Element(s) to show and hide below header
   */
  content: React.ReactNode;

  /**
   * Optional element to render when main content is hidden.
   */
  closedPlaceholder?: React.ReactNode;

  /**
   * Allows positioning of toggle button
   * useful when the header is smaller than the drawer,
   * or a tall multiline item.
   *
   * @default 'right'
   * defaults to center-aligned away from header element
   */
  buttonPosition?:
    | 'left'
    | 'right'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';

  /**
   * Option to set default open/close behavior.
   * @default false
   */
  autoCollapse?: boolean;

  /**
   * Option to dynamically autocollapse
   * depending on how many items are passed as content.
   * Useful for dynamically showing/hiding lists of items
   */
  autoCollapseAfter?: number;
}

export const CollapsibleDrawer = ({
  className,
  header,
  content,
  closedPlaceholder,
  buttonPosition = 'right',
  autoCollapse = false,
  autoCollapseAfter,
}: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(
    !(autoCollapse || dynamicAutoCollapse(autoCollapseAfter, content)),
  );
  const toggleDrawer = () => setDrawerOpen(!drawerOpen);

  return (
    <div className={classnames(className)}>
      {/* Header slot */}
      <div className="flex items-center">
        {header}
        <div
          className={classnames('flex flex-1 self-stretch', {
            'items-center justify-start': buttonPosition === 'left',
            'items-center justify-end': buttonPosition === 'right',
            'items-start justify-start': buttonPosition === 'top-left',
            'items-start justify-end': buttonPosition === 'top-right',
            'items-end justify-start': buttonPosition === 'bottom-left',
            'items-end justify-end': buttonPosition === 'bottom-right',
          })}
        >
          <IconButton
            aria-label={drawerOpen ? 'Collapse drawer' : 'Open drawer'}
            variant="hidden"
            size="sm"
            icon={drawerOpen ? <IconChevronUp /> : <IconChevronDown />}
            onClick={toggleDrawer}
          />
        </div>
      </div>
      {/* Drawer Slot */}
      <div>
        {/* Open Drawer/Content */}
        <div className={classnames({ hidden: !drawerOpen })}>{content}</div>
        {/* Closed Drawer Placeholder */}
        {closedPlaceholder && (
          <div className={classnames({ hidden: drawerOpen })}>
            {closedPlaceholder}
          </div>
        )}
      </div>
    </div>
  );
};
